// Firebase service adapter for web admin dashboard
// This file implements real Firebase database connections

// Import Firebase modules
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  signOut 
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc,
  updateDoc,
  deleteDoc,
  query, 
  orderBy, 
  limit, 
  where,
  onSnapshot,
  Timestamp,
  runTransaction,
  setDoc
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';

// Import Firebase config
import { auth as firebaseAuth, db as firebaseDb, storage as firebaseStorage, functions as firebaseFunctions } from '../config/firebase';
import firebaseApp from '../config/firebase';

// Use the configured Firebase instances
const auth = firebaseAuth;
const db = firebaseDb;
const storage = firebaseStorage;
const functions = firebaseFunctions;

// TypeScript interfaces for Firebase data
interface FirebaseAppointment {
  id: string;
  patientName?: string;
  doctorName?: string;
  department?: string;
  date?: string;
  appointmentDate?: string;
  time?: string;
  status?: string;
  amount?: string | number;
  fee?: string | number;
  createdAt?: string;
  [key: string]: any;
}

interface FirebaseUser {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  [key: string]: any;
}

interface FirebaseDoctor {
  id: string;
  name?: string;
  department?: string;
  isActive?: boolean;
  [key: string]: any;
}

interface FirebasePayment {
  id: string;
  amount?: string | number;
  date?: string;
  status?: string;
  patientId?: string;
  patientName?: string;
  description?: string;
  type?: string;
  paymentMethod?: string;
  transactionId?: string;
  items?: Array<{
    name: string;
    amount: number;
  }>;
  [key: string]: any;
}

interface FirebasePatient {
  id: string;
  name?: string;
  age?: number;
  gender?: string;
  phone?: string;
  patientType?: 'IP' | 'OP';
  doctor?: string;
  department?: string;
  bloodGroup?: string;
  registrationDate?: string;
  room?: string;
  bedNo?: string;
  admissionDate?: string;
  [key: string]: any;
}

interface FirebaseMedicalReport {
  id: string;
  patientId?: string;
  patientName?: string;
  reportType?: string;
  date?: string;
  doctor?: string;
  department?: string;
  status?: string;
  findings?: string;
  recommendations?: string;
  [key: string]: any;
}

// Utility function to convert Firebase Timestamp to readable format
const convertFirebaseTimestamp = (timestamp: any): string => {
  if (!timestamp) return '';
  
  // If it's already a string, parse and format it
  if (typeof timestamp === 'string') {
    // Handle format like "2025-11-28 10:00 AM" - extract just the date part
    if (timestamp.includes(' ')) {
      const datePart = timestamp.split(' ')[0]; // Get "2025-11-28"
      return datePart;
    }
    // If it's already in YYYY-MM-DD format or similar
    if (timestamp.includes('-')) {
      return timestamp.split('T')[0]; // Handle ISO format
    }
    return timestamp;
  }
  
  // If it's a Firebase Timestamp object
  if (timestamp && typeof timestamp === 'object' && ('seconds' in timestamp || 'nanoseconds' in timestamp)) {
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
      return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
    } catch (error) {
      console.warn('Error converting timestamp:', error);
      return '';
    }
  }
  
  // If it's a Date object
  if (timestamp instanceof Date) {
    return timestamp.toISOString().split('T')[0];
  }
  
  // If it's a number (unix timestamp)
  if (typeof timestamp === 'number') {
    return new Date(timestamp).toISOString().split('T')[0];
  }
  
  return '';
};

const convertFirebaseTime = (timeValue: any): string => {
  if (!timeValue) return '10:00 AM';
  
  // If it's already a string, return as is
  if (typeof timeValue === 'string') return timeValue;
  
  // If it's a Firebase Timestamp object, convert to time format
  if (timeValue && typeof timeValue === 'object' && ('seconds' in timeValue || 'nanoseconds' in timeValue)) {
    try {
      const date = timeValue.toDate ? timeValue.toDate() : new Date(timeValue.seconds * 1000);
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch (error) {
      console.warn('Error converting time:', error);
      return '10:00 AM';
    }
  }
  
  return '10:00 AM';
};

// Admin Authentication Service
class AdminAuthService {
  static async login(email: string, password: string) {
    try {
      console.log('🔐 Attempting admin login for:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Only store essential admin data, let Firebase handle auth persistence
      localStorage.setItem('isAuthenticated', 'true');
      
      console.log('✅ Admin login successful');
      return user;
    } catch (error) {
      console.error('❌ Login error:', error);
      throw error;
    }
  }

  static async logout() {
    try {
      console.log('🚪 Performing admin logout...');
      
      // Clear authentication data from localStorage
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('adminData');
      localStorage.removeItem('adminEmail');
      localStorage.removeItem('adminDocId');
      localStorage.removeItem('adminRole');
      
      // Sign out from Firebase
      await signOut(auth);
      
      console.log('✅ Admin logout completed');
    } catch (error) {
      console.error('❌ Logout error:', error);
      throw error;
    }
  }

  static onAuthStateChanged(callback: (user: any) => void) {
    return onAuthStateChanged(auth, (user) => {
      // Update last activity when auth state changes
      if (user) {
        this.updateLastActivity();
      }
      callback(user);
    });
  }

  static getCurrentUser() {
    const firebaseUser = auth.currentUser;
    
    // If Firebase user exists, update activity and return user
    if (firebaseUser) {
      this.updateLastActivity();
      return firebaseUser;
    }
    
    // If no Firebase user, ensure localStorage is cleared
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (isAuthenticated === 'true' && !firebaseUser) {
      console.warn('⚠️ No Firebase user but localStorage shows authenticated, clearing');
      localStorage.removeItem('isAuthenticated');
    }
    
    return null;
  }
  
  static isSessionValid(): boolean {
    try {
      // Primarily rely on Firebase auth state
      const firebaseUser = auth.currentUser;
      const isAuthenticated = localStorage.getItem('isAuthenticated');
      
      // If Firebase user exists and localStorage confirms authentication, session is valid
      if (firebaseUser && isAuthenticated === 'true') {
        return true;
      }
      
      // If either is missing, session is invalid
      if (!firebaseUser || isAuthenticated !== 'true') {
        return false;
      }
      
      return false;
    } catch (error) {
      console.error('❌ Error checking session validity:', error);
      localStorage.removeItem('isAuthenticated');
      return false;
    }
  }
  
  static updateLastActivity(): void {
    // Update activity timestamp for basic session tracking
    localStorage.setItem('lastActivity', new Date().toISOString());
  }
  
  static clearStoredSession(): void {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('adminData');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminDocId');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('lastActivity');
  }

  // Verify if the current user is an authorized admin
  static async verifyAdminAccess(): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;
      
      if (!currentUser || !currentUser.email) {
        console.error('❌ No authenticated user found');
        return false;
      }

      // Check if user exists in adminUsers collection
      const adminsRef = collection(db, 'adminUsers');
      const q = query(adminsRef, where('email', '==', currentUser.email));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.error('❌ User not authorized as admin:', currentUser.email);
        return false;
      }

      console.log('✅ Admin access verified for:', currentUser.email);
      return true;
    } catch (error) {
      console.error('❌ Error verifying admin access:', error);
      return false;
    }
  }
}

// Dashboard Stats Service
class DashboardService {
  static async getDashboardStats() {
    try {
      console.log('📊 Fetching dashboard stats...');
      
      // Get all appointments
      const appointmentsRef = collection(db, 'appointments');
      const appointmentsSnapshot = await getDocs(appointmentsRef);
      const appointments: FirebaseAppointment[] = appointmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get all users
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const users: FirebaseUser[] = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get all doctors
      const doctorsRef = collection(db, 'doctors');
      const doctorsSnapshot = await getDocs(doctorsRef);
      const doctors: FirebaseDoctor[] = doctorsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get all payments
      const paymentsRef = collection(db, 'payments');
      const paymentsSnapshot = await getDocs(paymentsRef);
      const payments: FirebasePayment[] = paymentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get all invoices
      const invoicesRef = collection(db, 'invoices');
      const invoicesSnapshot = await getDocs(invoicesRef);
      const invoices: any[] = invoicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get all medical reports
      const medicalReportsRef = collection(db, 'medicalReports');
      const medicalReportsSnapshot = await getDocs(medicalReportsRef);
      const medicalReports: any[] = medicalReportsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      console.log('📊 Firebase collection counts:', {
        appointments: appointments.length,
        users: users.length,
        doctors: doctors.length,
        payments: payments.length,
        invoices: invoices.length,
        medicalReports: medicalReports.length
      });
      
      // If all collections are empty, immediately return fallback data
      if (appointments.length === 0 && users.length === 0 && payments.length === 0) {
        console.log('📊 Collections are empty - using fallback data');
        return this.getFallbackDashboardData();
      }
      
      // Calculate stats
      const totalUsers = users.length;
      const totalAppointments = appointments.length;
      
      // Helper function to check if a doctor is currently available
      const isDoctorCurrentlyAvailable = (doc: any): boolean => {
        if (doc.status !== 'Active') return false;
        
        const now = new Date();
        const currentDay = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][now.getDay()];
        
        // Check if today is in doctor's schedule
        if (!doc.schedule || !doc.schedule.includes(currentDay)) {
          return false;
        }

        // Parse time slots (e.g., "9:00 AM - 6:00 PM")
        if (!doc.timeSlots) return false;
        
        const timeMatch = doc.timeSlots.match(/(\d{1,2}:\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}:\d{2})\s*(AM|PM)/i);
        if (!timeMatch) return false;

        const [_, startTime, startPeriod, endTime, endPeriod] = timeMatch;
        
        // Convert to 24-hour format
        const parseTime = (time: string, period: string): number => {
          const [hours, minutes] = time.split(':').map(Number);
          let hour24 = hours;
          if (period.toUpperCase() === 'PM' && hours !== 12) hour24 += 12;
          if (period.toUpperCase() === 'AM' && hours === 12) hour24 = 0;
          return hour24 * 60 + minutes;
        };

        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        const startMinutes = parseTime(startTime, startPeriod);
        const endMinutes = parseTime(endTime, endPeriod);

        return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
      };
      
      // Count active doctors based on status, schedule, and current time
      const activeDoctors = doctors.filter(isDoctorCurrentlyAvailable).length;
      
      // Debug logging for doctors
      console.log('👨‍⚕️ Doctors in database:', doctors.map(d => ({
        name: d.name,
        status: d.status,
        schedule: d.schedule,
        timeSlots: d.timeSlots,
        isCurrentlyAvailable: isDoctorCurrentlyAvailable(d)
      })));
      console.log(`👨‍⚕️ Total doctors: ${doctors.length}, Active doctors (currently available): ${activeDoctors}`);
      
      // Calculate revenue using same logic as Payments page
      // Group payments by patientId and get latest payment per patient
      const latestPaymentsByPatient = new Map<string, any>();
      
      payments.forEach(payment => {
        if (!payment.patientId) return;
        
        const existing = latestPaymentsByPatient.get(payment.patientId);
        if (!existing) {
          latestPaymentsByPatient.set(payment.patientId, payment);
        } else {
          // Keep the latest payment (newest date)
          const existingDate = new Date(existing.lastPaymentDate || existing.date || existing.createdAt || 0).getTime();
          const currentDate = new Date(payment.lastPaymentDate || payment.date || payment.createdAt || 0).getTime();
          
          if (currentDate > existingDate) {
            latestPaymentsByPatient.set(payment.patientId, payment);
          }
        }
      });
      
      // Sum paidAmount from latest payment cards only (matching Payments page logic)
      const totalRevenue = Array.from(latestPaymentsByPatient.values()).reduce((sum, payment) => {
        const paidAmount = parseFloat(String(payment.paidAmount || 0)) || 0;
        return sum + paidAmount;
      }, 0);
      
      console.log('💰 Revenue Calculation (matching Payments page):');
      console.log(`   Total patients with payments: ${latestPaymentsByPatient.size}`);
      console.log(`   Total Revenue (sum of paidAmount from latest payments): ₹${totalRevenue}`);
      
      // Calculate today's appointments
      const today = new Date().toISOString().split('T')[0];
      const todayAppointments = appointments.filter(appointment => {
        const appointmentDate = appointment.date?.split('T')[0] || appointment.appointmentDate?.split('T')[0];
        return appointmentDate === today;
      }).length;
      
      // Get recent appointments (last 5)
      const recentAppointments = appointments
        .sort((a, b) => {
          const dateA = new Date(a.date || a.appointmentDate || a.createdAt || '');
          const dateB = new Date(b.date || b.appointmentDate || b.createdAt || '');
          return dateB.getTime() - dateA.getTime();
        })
        .slice(0, 5)
        .map(appointment => ({
          id: appointment.id,
          patientName: appointment.patientName || 'Unknown Patient',
          doctor: `${appointment.doctorName || 'Unknown Doctor'} • ${appointment.department || 'General'}`,
          time: this.formatAppointmentTime(appointment),
          status: appointment.status || 'scheduled',
          avatar: (appointment.patientName || 'U').charAt(0).toUpperCase()
        }));
      
      // Count pending appointments
      const pendingInvoicesCount = appointments.filter(appointment => 
        appointment.status === 'pending' || appointment.status === 'scheduled'
      ).length;
      
      const stats = {
        totalUsers,
        totalAppointments,
        totalRevenue,
        activeDoctors,
        todayAppointments,
        recentAppointments,
        pendingInvoicesCount
      };
      
      console.log('📈 Dashboard stats calculated:', stats);
      return stats;
      
    } catch (error) {
      console.error('❌ Error fetching dashboard stats:', error);
      // Return comprehensive fallback data if Firebase fails
      return this.getFallbackDashboardData();
    }
  }

  // Real-time dashboard stats with live listeners
  static subscribeToDashboardStats(callback: (stats: any) => void) {
    try {
      console.log('🔄 Setting up real-time dashboard listeners...');
      
      let appointmentsData: any[] = [];
      let usersData: any[] = [];
      let doctorsData: any[] = [];
      let paymentsData: any[] = [];
      let invoicesData: any[] = [];
      let medicalReportsData: any[] = [];
      let dataInitialized = false;
      
      const calculateAndEmitStats = () => {
        try {
          console.log('🔄 Calculating stats from data:', {
            users: usersData.length,
            appointments: appointmentsData.length,
            doctors: doctorsData.length,
            payments: paymentsData.length,
            invoices: invoicesData.length,
            medicalReports: medicalReportsData.length
          });
          
          // Calculate stats from live data
          const totalUsers = usersData.length;
          const totalAppointments = appointmentsData.length;
          
          // Helper function to check if a doctor is currently available
          const isDoctorCurrentlyAvailable = (doc: any): boolean => {
            if (doc.status !== 'Active') return false;
            
            const now = new Date();
            const currentDay = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][now.getDay()];
            
            // Check if today is in doctor's schedule
            if (!doc.schedule || !doc.schedule.includes(currentDay)) {
              return false;
            }

            // Parse time slots (e.g., "9:00 AM - 6:00 PM")
            if (!doc.timeSlots) return false;
            
            const timeMatch = doc.timeSlots.match(/(\d{1,2}:\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}:\d{2})\s*(AM|PM)/i);
            if (!timeMatch) return false;

            const [_, startTime, startPeriod, endTime, endPeriod] = timeMatch;
            
            // Convert to 24-hour format
            const parseTime = (time: string, period: string): number => {
              const [hours, minutes] = time.split(':').map(Number);
              let hour24 = hours;
              if (period.toUpperCase() === 'PM' && hours !== 12) hour24 += 12;
              if (period.toUpperCase() === 'AM' && hours === 12) hour24 = 0;
              return hour24 * 60 + minutes;
            };

            const currentMinutes = now.getHours() * 60 + now.getMinutes();
            const startMinutes = parseTime(startTime, startPeriod);
            const endMinutes = parseTime(endTime, endPeriod);

            return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
          };
          
          // Count active doctors based on status, schedule, and current time
          const activeDoctors = doctorsData.filter(isDoctorCurrentlyAvailable).length;
          
          // Debug logging for doctors
          console.log('👨‍⚕️ Doctors status breakdown:', doctorsData.map(d => ({
            name: d.name,
            status: d.status,
            schedule: d.schedule,
            timeSlots: d.timeSlots,
            isCurrentlyAvailable: isDoctorCurrentlyAvailable(d)
          })));
          console.log(`👨‍⚕️ Total doctors: ${doctorsData.length}, Active doctors (currently available): ${activeDoctors}`);
          
          // Calculate revenue using same logic as Payments page
          // Group payments by patientId and get latest payment per patient
          const latestPaymentsByPatient = new Map<string, any>();
          
          paymentsData.forEach(payment => {
            if (!payment.patientId) return;
            
            const existing = latestPaymentsByPatient.get(payment.patientId);
            if (!existing) {
              latestPaymentsByPatient.set(payment.patientId, payment);
            } else {
              // Keep the latest payment (newest date)
              const existingDate = new Date(existing.lastPaymentDate || existing.date || existing.createdAt || 0).getTime();
              const currentDate = new Date(payment.lastPaymentDate || payment.date || payment.createdAt || 0).getTime();
              
              if (currentDate > existingDate) {
                latestPaymentsByPatient.set(payment.patientId, payment);
              }
            }
          });
          
          // Sum paidAmount from latest payment cards only (matching Payments page logic)
          const totalRevenue = Array.from(latestPaymentsByPatient.values()).reduce((sum, payment) => {
            const paidAmount = parseFloat(String(payment.paidAmount || 0)) || 0;
            return sum + paidAmount;
          }, 0);
          
          console.log('💰 Revenue Calculation (matching Payments page):');
          console.log(`   Total patients with payments: ${latestPaymentsByPatient.size}`);
          console.log(`   Total Revenue (sum of paidAmount from latest payments): ₹${totalRevenue}`);
          
          // Calculate today's appointments
          const today = new Date().toISOString().split('T')[0];
          const todayAppointments = appointmentsData.filter(appointment => {
            const appointmentDate = convertFirebaseTimestamp(appointment.appointmentDate || appointment.date);
            return appointmentDate === today;
          }).length;
          
          // Get recent appointments (last 5)
          const recentAppointments = appointmentsData
            .sort((a, b) => {
              const dateA = new Date(a.date || a.appointmentDate || a.createdAt || '');
              const dateB = new Date(b.date || b.appointmentDate || b.createdAt || '');
              return dateB.getTime() - dateA.getTime();
            })
            .slice(0, 5)
            .map(appointment => ({
              id: appointment.id,
              patientName: appointment.patientName || 'Unknown Patient',
              doctor: `${appointment.doctorName || 'Unknown Doctor'} • ${appointment.department || 'General'}`,
              time: this.formatAppointmentTimeRealtime(appointment),
              status: appointment.status || 'scheduled',
              avatar: (appointment.patientName || 'U').charAt(0).toUpperCase()
            }));
          
          // Count pending appointments
          const pendingInvoicesCount = appointmentsData.filter(appointment => 
            appointment.status === 'pending' || appointment.status === 'scheduled'
          ).length;
          
          const stats = {
            totalUsers,
            totalAppointments,
            totalRevenue,
            activeDoctors,
            todayAppointments,
            recentAppointments,
            pendingInvoicesCount
          };
          
          // If all collections are empty, always use fallback data
          if (totalUsers === 0 && totalAppointments === 0 && totalRevenue === 0) {
            console.log('📊 Using fallback data - collections are empty');
            const fallbackStats = this.getFallbackDashboardData();
            callback(fallbackStats);
            dataInitialized = true;
            return;
          }
          
          dataInitialized = true;
          console.log('📊 Real-time dashboard stats:', stats);
          callback(stats);
        } catch (error) {
          console.error('❌ Error calculating dashboard stats:', error);
          // Use fallback data on error
          const fallbackStats = this.getFallbackDashboardData();
          callback(fallbackStats);
        }
      };
      
      // Set up listeners for all collections
      const unsubscribeAppointments = onSnapshot(collection(db, 'appointments'), (snapshot) => {
        appointmentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        calculateAndEmitStats();
      });
      
      const unsubscribeUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
        usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        calculateAndEmitStats();
      });
      
      const unsubscribeDoctors = onSnapshot(collection(db, 'doctors'), (snapshot) => {
        doctorsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        calculateAndEmitStats();
      });
      
      const unsubscribePayments = onSnapshot(collection(db, 'payments'), (snapshot) => {
        paymentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        calculateAndEmitStats();
      });
      
      const unsubscribeInvoices = onSnapshot(collection(db, 'invoices'), (snapshot) => {
        invoicesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        calculateAndEmitStats();
      });
      
      const unsubscribeMedicalReports = onSnapshot(collection(db, 'medicalReports'), (snapshot) => {
        medicalReportsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        calculateAndEmitStats();
      });
      
      // Return cleanup function
      return () => {
        unsubscribeAppointments();
        unsubscribeUsers();
        unsubscribeDoctors();
        unsubscribePayments();
        unsubscribeInvoices();
        unsubscribeMedicalReports();
      };
      
    } catch (error) {
      console.error('❌ Error setting up dashboard listeners:', error);
      console.log('📊 Using fallback data due to connection issues');
      
      // Return fallback data immediately and set up a cleanup function
      const fallbackStats = this.getFallbackDashboardData();
      setTimeout(() => callback(fallbackStats), 100);
      
      // Return a dummy cleanup function
      return () => {
        console.log('🧹 Cleanup function called (fallback mode)');
      };
    }
  }

  // Get comprehensive fallback data for dashboard
  static getFallbackDashboardData() {
    const today = new Date().toISOString().split('T')[0];
    
    return {
      totalUsers: 25,
      totalAppointments: 45,
      totalRevenue: 18500,
      activeDoctors: 0,
      todayAppointments: 8,
      recentAppointments: [
        {
          id: 'fallback-1',
          patientName: 'Rajesh Kumar',
          doctor: 'Dr. Shital M • General Medicine',
          time: `${today} 10:30 AM • ₹500`,
          status: 'confirmed',
          avatar: 'R'
        },
        {
          id: 'fallback-2',
          patientName: 'Priya Sharma',
          doctor: 'Dr. Kani Basha • Cardiology',
          time: `${today} 11:00 AM • ₹800`,
          status: 'scheduled',
          avatar: 'P'
        },
        {
          id: 'fallback-3',
          patientName: 'Amit Patel',
          doctor: 'Dr. Test Technician • Laboratory',
          time: `${today} 02:30 PM • ₹1200`,
          status: 'completed',
          avatar: 'A'
        },
        {
          id: 'fallback-4',
          patientName: 'Sunita Rao',
          doctor: 'Dr. Ramesh Kumar • Gynecology',
          time: `${today} 03:00 PM • ₹600`,
          status: 'pending',
          avatar: 'S'
        },
        {
          id: 'fallback-5',
          patientName: 'Vikash Singh',
          doctor: 'Dr. Anita Singh • Pediatrics',
          time: `${today} 04:30 PM • ₹550`,
          status: 'scheduled',
          avatar: 'V'
        }
      ],
      pendingInvoicesCount: 12
    };
  }

  // Format appointment time helper for real-time updates
  static formatAppointmentTimeRealtime(appointment: any): string {
    try {
      const date = convertFirebaseTimestamp(appointment.appointmentDate || appointment.date);
      const time = convertFirebaseTime(appointment.time || appointment.appointmentTime);
      const fee = appointment.fee || appointment.amount;
      return fee ? `${date} ${time} • ₹${fee}` : `${date} ${time} • Contact Hospital`;
    } catch (error) {
      return '2025-11-19 10:30 AM • Contact Hospital';
    }
  }
  
  static formatAppointmentTime(appointment: FirebaseAppointment) {
    try {
      const date = new Date(appointment.date || appointment.appointmentDate || appointment.createdAt || '');
      const time = appointment.time || '10:30 AM';
      const amount = appointment.amount || appointment.fee;
      
      const dateStr = date.toLocaleDateString('en-GB');
      return amount ? `${dateStr} ${time} • ₹${amount}` : `${dateStr} ${time} • Contact Hospital`;
    } catch (error) {
      return 'Date not available • Contact Hospital';
    }
  }

  static async getPatients() {
    try {
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      return usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching patients:', error);
      return [];
    }
  }

  static async getDoctors() {
    try {
      const doctorsRef = collection(db, 'doctors');
      const doctorsSnapshot = await getDocs(doctorsRef);
      return doctorsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching doctors:', error);
      return [];
    }
  }

  static async getAppointments() {
    try {
      const appointmentsRef = collection(db, 'appointments');
      const appointmentsSnapshot = await getDocs(appointmentsRef);
      return appointmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching appointments:', error);
      return [];
    }
  }

  static async getPayments() {
    try {
      const paymentsRef = collection(db, 'payments');
      const paymentsSnapshot = await getDocs(paymentsRef);
      return paymentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching payments:', error);
      return [];
    }
  }

  // Get available rooms for patient assignment
  static async getRooms(): Promise<any[]> {
    try {
      const roomsRef = collection(db, 'rooms');
      const roomsSnapshot = await getDocs(roomsRef);
      const firebaseRooms = roomsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Transform Firebase room data to expected format
      return firebaseRooms.map((room: any) => ({
        id: room.id,
        roomNumber: room.roomNumber || room.number || room.id,
        roomType: room.roomType || room.type || 'general',
        capacity: room.capacity || 2,
        currentOccupancy: room.currentOccupancy || 0,
        department: room.department || 'General',
        status: room.status || 'available',
        availableBeds: room.availableBeds || [
          { bedNumber: 'A', isOccupied: false },
          { bedNumber: 'B', isOccupied: false }
        ]
      }));
    } catch (error) {
      console.error('Error fetching rooms:', error);
      // Return fallback room data
      return [
        {
          id: '1',
          roomNumber: '101',
          roomType: 'general',
          capacity: 2,
          currentOccupancy: 0,
          department: 'General Medicine',
          status: 'available',
          availableBeds: [
            { bedNumber: 'A', isOccupied: false },
            { bedNumber: 'B', isOccupied: false }
          ]
        },
        {
          id: '2',
          roomNumber: 'C1',
          roomType: 'consultation',
          capacity: 1,
          currentOccupancy: 0,
          department: 'General',
          status: 'available',
          availableBeds: [
            { bedNumber: '1', isOccupied: false }
          ]
        }
      ];
    }
  }

  // Real-time listener for dashboard updates
  static onDashboardDataChange(callback: (data: any) => void): () => void {
    const unsubscribers: (() => void)[] = [];
    
    // Listen to appointments changes
    const appointmentsRef = collection(db, 'appointments');
    const unsubAppointments = onSnapshot(appointmentsRef, () => {
      this.getDashboardStats().then(callback);
    });
    unsubscribers.push(unsubAppointments);
    
    // Listen to users changes
    const usersRef = collection(db, 'users');
    const unsubUsers = onSnapshot(usersRef, () => {
      this.getDashboardStats().then(callback);
    });
    unsubscribers.push(unsubUsers);
    
    // Listen to payments changes
    const paymentsRef = collection(db, 'payments');
    const unsubPayments = onSnapshot(paymentsRef, () => {
      this.getDashboardStats().then(callback);
    });
    unsubscribers.push(unsubPayments);
    
    // Return function to unsubscribe all listeners
    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }
}

// Export Firebase instances for direct use
export { auth, db, storage, functions };

// Enhanced Appointment Management Service with real Firebase integration
class AppointmentManagementService {
  static collectionName = 'appointments';

  // Get all appointments with proper data transformation
  static async getAppointments() {
    try {
      console.log('📋 Fetching appointments from Firebase...');
      
      const appointmentsRef = collection(db, this.collectionName);
      const q = query(appointmentsRef);
      
      const querySnapshot = await getDocs(q);
      const appointments: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        appointments.push({
          id: doc.id,
          firebaseDocId: doc.id,
          ...data,
          // Normalize field names with proper timestamp conversion
          patientName: data.patientName || 'Unknown Patient',
          doctorName: data.doctorName || data.doctor || 'Unknown Doctor',
          department: data.department || 'General',
          appointmentDate: convertFirebaseTimestamp(data.appointmentDate || data.date),
          date: convertFirebaseTimestamp(data.appointmentDate || data.date),
          time: convertFirebaseTime(data.time || data.appointmentTime),
          appointmentTime: convertFirebaseTime(data.time || data.appointmentTime),
          status: data.status || 'scheduled',
          contactNumber: data.contactNumber || data.phone,
          service: data.service || data.serviceName || 'Consultation',
          fee: data.fee || data.amount || 500,
          paymentMode: data.paymentMode || 'Cash',
        });
      });
      
      // Sort by date (newest first)
      appointments.sort((a, b) => {
        const dateA = new Date(a.appointmentDate || a.date || 0);
        const dateB = new Date(b.appointmentDate || b.date || 0);
        return dateB.getTime() - dateA.getTime();
      });
      
      console.log(`✅ Fetched ${appointments.length} appointments from Firebase`);
      return appointments;
    } catch (error) {
      console.error('❌ Error fetching appointments:', error);
      return [];
    }
  }

  // Create new appointment
  static async createAppointment(appointmentData: any) {
    try {
      console.log('📝 Creating appointment in Firebase...', appointmentData);
      
      // Generate sequential appointment number using transaction
      const counterRef = doc(db, 'counters/appointmentCounter');
      let appointmentNumber = '';
      
      await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);
        
        let nextNumber = 1;
        
        if (counterDoc.exists()) {
          const currentCount = counterDoc.data().count || 0;
          nextNumber = currentCount + 1;
        } else {
          // First appointment or counter reset
          const appointmentsSnapshot = await getDocs(collection(db, this.collectionName));
          if (!appointmentsSnapshot.empty) {
            // Rebuild counter from existing appointments
            let maxNumber = 0;
            appointmentsSnapshot.forEach((doc) => {
              const data = doc.data();
              if (data.appointmentNumber) {
                const match = data.appointmentNumber.match(/KBR\s+(\d+)/);
                if (match) {
                  const num = parseInt(match[1], 10);
                  if (num > maxNumber) maxNumber = num;
                }
              }
            });
            nextNumber = maxNumber + 1;
          }
        }
        
        // Update counter
        transaction.set(counterRef, {
          count: nextNumber,
          lastUpdated: new Date().toISOString()
        });
        
        // Format appointment number: KBR 001
        appointmentNumber = `KBR ${String(nextNumber).padStart(3, '0')}`;
      });
      
      const appointment = {
        ...appointmentData,
        appointmentNumber: appointmentNumber,
        status: appointmentData.status || 'scheduled',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        tokenNumber: `TKN-${Date.now()}` // Generate simple token
      };

      const docRef = await addDoc(collection(db, this.collectionName), appointment);
      console.log(`✅ Appointment created with ID: ${docRef.id} and number: ${appointmentNumber}`);
      
      return {
        success: true,
        data: {
          id: docRef.id,
          ...appointment
        }
      };
    } catch (error: any) {
      console.error('❌ Error creating appointment:', error);
      throw new Error(`Failed to create appointment: ${error?.message || error}`);
    }
  }

  // Real-time listener for appointments
  static listenToAppointments(callback: (appointments: any[]) => void) {
    try {
      console.log('🔄 Setting up real-time listener for appointments...');
      
      const appointmentsRef = collection(db, this.collectionName);
      const q = query(appointmentsRef);
      
      // Set up real-time listener
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        console.log(`📊 Firestore snapshot received: ${querySnapshot.size} documents`);
        const appointments: any[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          console.log(`📄 Processing document ${doc.id}:`, data);
          
          const appointment = {
            id: doc.id,
            firebaseDocId: doc.id,
            ...data,
            // Normalize field names with proper timestamp conversion
            patientName: data.patientName || 'Unknown Patient',
            doctorName: data.doctorName || data.doctor || 'Unknown Doctor',
            department: data.department || 'General',
            appointmentDate: convertFirebaseTimestamp(data.appointmentDate || data.date),
            date: convertFirebaseTimestamp(data.appointmentDate || data.date),
            time: convertFirebaseTime(data.time || data.appointmentTime),
            appointmentTime: convertFirebaseTime(data.time || data.appointmentTime),
            status: data.status || 'scheduled',
            contactNumber: data.contactNumber || data.phone,
            service: data.service || data.serviceName || 'Consultation',
            fee: data.fee || data.amount || null,
            paymentMode: data.paymentMode || 'Cash',
          };
          
          console.log(`✅ Normalized appointment ${doc.id}:`, appointment);
          appointments.push(appointment);
        });
        
        // Sort by date (newest first)
        appointments.sort((a, b) => {
          const dateA = new Date(a.appointmentDate || a.date || 0);
          const dateB = new Date(b.appointmentDate || b.date || 0);
          return dateB.getTime() - dateA.getTime();
        });
        
        console.log(`🔄 Real-time update complete: ${appointments.length} appointments ready to send to callback`);
        callback(appointments);
      }, (error) => {
        console.error('❌ Error in appointments listener:', error);
        callback([]);
      });
      
      console.log('✅ Real-time listener successfully attached');
      return unsubscribe;
    } catch (error) {
      console.error('❌ Error setting up appointments listener:', error);
      return () => {}; // Return empty unsubscribe function
    }
  }

  // Update appointment status
  static async updateAppointmentStatus(appointmentId: string, status: string) {
    try {
      console.log(`📝 Updating appointment ${appointmentId} status to ${status}`);
      
      // First check if document exists
      const appointmentRef = doc(db, this.collectionName, appointmentId);
      const appointmentDoc = await getDoc(appointmentRef);
      
      if (!appointmentDoc.exists()) {
        console.error(`❌ Appointment ${appointmentId} not found in database`);
        throw new Error('No document to update - appointment not found');
      }
      
      // Document exists, proceed with update
      await updateDoc(appointmentRef, {
        status: status,
        updatedAt: new Date().toISOString()
      });
      
      console.log(`✅ Appointment ${appointmentId} status updated to ${status}`);
      return {
        success: true,
        message: `Appointment ${status} successfully`
      };
    } catch (error: any) {
      console.error('❌ Error updating appointment status:', error);
      
      // Provide more specific error message
      if (error.message.includes('No document to update')) {
        throw error; // Re-throw with same message
      }
      
      throw new Error(`Failed to update appointment status: ${error?.message || error}`);
    }
  }

  // Update appointment data
  static async updateAppointment(appointmentId: string, updateData: any) {
    try {
      console.log(`📝 Updating appointment ${appointmentId} with data:`, updateData);
      
      // First check if document exists
      const appointmentRef = doc(db, this.collectionName, appointmentId);
      const appointmentDoc = await getDoc(appointmentRef);
      
      if (!appointmentDoc.exists()) {
        console.error(`❌ Appointment ${appointmentId} not found in database`);
        throw new Error('No document to update - appointment not found');
      }
      
      // Document exists, proceed with update
      await updateDoc(appointmentRef, {
        ...updateData,
        updatedAt: new Date().toISOString()
      });
      
      console.log(`✅ Appointment ${appointmentId} updated successfully`);
      return {
        success: true,
        message: 'Appointment updated successfully'
      };
    } catch (error: any) {
      console.error('❌ Error updating appointment:', error);
      
      if (error.message.includes('No document to update')) {
        throw error;
      }
      
      throw new Error(`Failed to update appointment: ${error?.message || error}`);
    }
  }
}
// Enhanced Patient Management Service
class PatientService {
  // Get all patients with filtering
  static async getPatients(patientType?: 'IP' | 'OP'): Promise<FirebasePatient[]> {
    try {
      let patientsQuery = query(
        collection(db, 'patients'),
        orderBy('registrationDate', 'desc')
      );

      if (patientType) {
        patientsQuery = query(
          collection(db, 'patients'),
          where('patientType', '==', patientType),
          orderBy('registrationDate', 'desc')
        );
      }

      const snapshot = await getDocs(patientsQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FirebasePatient));
    } catch (error) {
      console.error('Error fetching patients:', error);
      return [];
    }
  }

  // Get patient by ID
  static async getPatientById(patientId: string): Promise<FirebasePatient | null> {
    try {
      const patientDoc = await getDoc(doc(db, 'patients', patientId));
      if (patientDoc.exists()) {
        return { id: patientDoc.id, ...patientDoc.data() } as FirebasePatient;
      }
      return null;
    } catch (error) {
      console.error('Error fetching patient:', error);
      return null;
    }
  }

  // Get patient payment invoices
  static async getPatientPayments(patientId: string): Promise<FirebasePayment[]> {
    try {
      const paymentsQuery = query(
        collection(db, 'payments'),
        where('patientId', '==', patientId),
        orderBy('date', 'desc')
      );

      const snapshot = await getDocs(paymentsQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FirebasePayment));
    } catch (error) {
      console.error('Error fetching patient payments:', error);
      return [];
    }
  }

  // Get patient medical reports
  static async getPatientMedicalReports(patientId: string): Promise<FirebaseMedicalReport[]> {
    try {
      const reportsQuery = query(
        collection(db, 'medicalReports'),
        where('patientId', '==', patientId),
        orderBy('date', 'desc')
      );

      const snapshot = await getDocs(reportsQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FirebaseMedicalReport));
    } catch (error) {
      console.error('Error fetching medical reports:', error);
      return [];
    }
  }

  // Real-time patient updates (excludes discharged patients)
  static onPatientsSnapshot(callback: (patients: FirebasePatient[]) => void) {
    const patientsQuery = query(
      collection(db, 'patients'),
      orderBy('registrationDate', 'desc')
    );

    return onSnapshot(patientsQuery, (snapshot) => {
      const patients = snapshot.docs
        .map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        } as FirebasePatient))
        .filter(patient => patient.statusText !== 'Discharged'); // Exclude discharged patients
      callback(patients);
    });
  }

  // Real-time patient updates (includes ALL patients, even discharged ones)
  // Use this only for Discharge page to view discharge history
  static onAllPatientsSnapshot(callback: (patients: FirebasePatient[]) => void) {
    const patientsQuery = query(
      collection(db, 'patients'),
      orderBy('registrationDate', 'desc')
    );

    return onSnapshot(patientsQuery, (snapshot) => {
      const patients = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      } as FirebasePatient));
      callback(patients);
    });
  }

  // Real-time payment updates for a patient
  static onPatientPaymentsSnapshot(patientId: string, callback: (payments: FirebasePayment[]) => void) {
    const paymentsQuery = query(
      collection(db, 'payments'),
      where('patientId', '==', patientId),
      orderBy('date', 'desc')
    );

    return onSnapshot(paymentsQuery, (snapshot) => {
      const payments = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      } as FirebasePayment));
      callback(payments);
    });
  }
}

// Doctor Management Service
class DoctorManagementService {
  static collectionName = 'doctors';

  // Get all doctors with proper data transformation
  static async getDoctors() {
    try {
      console.log('👩‍⚕️ Fetching doctors from Firebase...');
      
      const doctorsRef = collection(db, this.collectionName);
      const q = query(doctorsRef, orderBy('name'));
      
      const querySnapshot = await getDocs(q);
      const doctors: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        doctors.push({
          id: doc.id,
          firebaseDocId: doc.id,
          ...data,
          // Normalize field names and ensure proper data types
          name: data.name || data.doctorName || 'Unknown Doctor',
          credentials: data.credentials || data.qualifications || 'MBBS',
          specialization: data.specialization || data.specialty || 'General Medicine',
          department: data.department || 'General',
          fellowship: data.fellowship || '',
          experience: data.experience || '1 year',
          rating: data.rating || 4.0,
          consultationFee: data.consultationFee || data.fee || 500,
          phone: data.phone || data.contactNumber || 'N/A',
          email: data.email || 'N/A',
          availability: data.availability || '6 days/week',
          schedule: data.schedule || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
          timeSlots: data.timeSlots || data.timing || '9:00 AM - 6:00 PM',
          expertise: data.expertise || data.specialties || ['General Medicine'],
          qualifications: data.qualifications || data.education || ['MBBS'],
          avatar: data.avatar || data.profileImage || '',
          status: this.normalizeStatus(data.status || data.isActive),
          statusColor: this.getStatusColor(data.status || data.isActive),
          todayAppointments: data.todayAppointments || 0,
          isActive: data.isActive !== false
        });
      });
      
      console.log(`📊 Found ${doctors.length} doctors in Firebase`);
      return doctors;
    } catch (error) {
      console.error('❌ Error fetching doctors:', error);
      throw error;
    }
  }

  // Get doctors with real-time updates
  static subscribeToDoctor(callback: (doctors: any[]) => void) {
    try {
      console.log('🔄 Setting up real-time doctors listener...');
      
      const doctorsRef = collection(db, this.collectionName);
      const q = query(doctorsRef, orderBy('name'));
      
      return onSnapshot(q, (querySnapshot) => {
        const doctors: any[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          doctors.push({
            id: doc.id,
            firebaseDocId: doc.id,
            ...data,
            // Normalize field names and ensure proper data types
            name: data.name || data.doctorName || 'Unknown Doctor',
            credentials: data.credentials || data.qualifications || 'MBBS',
            specialization: data.specialization || data.specialty || 'General Medicine',
            department: data.department || 'General',
            fellowship: data.fellowship || '',
            experience: data.experience || '1 year',
            rating: data.rating || 4.0,
            consultationFee: data.consultationFee || data.fee || 500,
            phone: data.phone || data.contactNumber || 'N/A',
            email: data.email || 'N/A',
            availability: data.availability || '6 days/week',
            schedule: data.schedule || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
            timeSlots: data.timeSlots || data.timing || '9:00 AM - 6:00 PM',
            expertise: data.expertise || data.specialties || ['General Medicine'],
            qualifications: data.qualifications || data.education || ['MBBS'],
            avatar: data.avatar || data.profileImage || '',
            status: this.normalizeStatus(data.status || data.isActive),
            statusColor: this.getStatusColor(data.status || data.isActive),
            todayAppointments: data.todayAppointments || 0,
            isActive: data.isActive !== false
          });
        });
        
        console.log(`🔄 Real-time update: ${doctors.length} doctors`);
        callback(doctors);
      });
    } catch (error) {
      console.error('❌ Error setting up doctors listener:', error);
      throw error;
    }
  }

  // Create a new doctor
  static async createDoctor(doctorData: any) {
    try {
      console.log('➕ Creating new doctor in Firebase...');
      
      const doctorsRef = collection(db, this.collectionName);
      const docRef = await addDoc(doctorsRef, {
        ...doctorData,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true
      });
      
      console.log('✅ Doctor created with ID:', docRef.id);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('❌ Error creating doctor:', error);
      throw error;
    }
  }

  // Update doctor
  static async updateDoctor(doctorId: string, updateData: any) {
    try {
      console.log('📝 Updating doctor in Firebase...', doctorId);
      
      const doctorRef = doc(db, this.collectionName, doctorId);
      await updateDoc(doctorRef, {
        ...updateData,
        updatedAt: new Date()
      });
      
      console.log('✅ Doctor updated successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating doctor:', error);
      throw error;
    }
  }

  // Delete doctor - Permanently removes doctor from database
  static async deleteDoctor(doctorId: string) {
    try {
      console.log('🗑️ Permanently deleting doctor from Firebase...', doctorId);
      
      const doctorRef = doc(db, this.collectionName, doctorId);
      
      // Check if doctor exists before deleting
      const doctorSnap = await getDoc(doctorRef);
      if (!doctorSnap.exists()) {
        console.warn('⚠️ Doctor not found:', doctorId);
        return { success: false, message: 'Doctor not found' };
      }
      
      // Permanently delete the doctor document
      await deleteDoc(doctorRef);
      
      console.log('✅ Doctor permanently deleted from Firebase');
      return { success: true, message: 'Doctor deleted successfully' };
    } catch (error) {
      console.error('❌ Error deleting doctor:', error);
      throw error;
    }
  }

  // Update doctor status
  static async updateDoctorStatus(doctorId: string, status: string) {
    try {
      console.log('🔄 Updating doctor status...', { doctorId, status });
      
      return await this.updateDoctor(doctorId, {
        status: status,
        isActive: status === 'Active'
      });
    } catch (error) {
      console.error('❌ Error updating doctor status:', error);
      throw error;
    }
  }

  // Helper methods
  private static normalizeStatus(status: any): string {
    if (!status) return 'Active';
    
    if (typeof status === 'boolean') {
      return status ? 'Active' : 'Inactive';
    }
    
    if (typeof status === 'string') {
      const statusLower = status.toLowerCase();
      if (statusLower.includes('active')) return 'Active';
      if (statusLower.includes('leave')) return 'On Leave';
      if (statusLower.includes('inactive')) return 'Inactive';
      return 'Active';
    }
    
    return 'Active';
  }

  private static getStatusColor(status: any): string {
    const normalizedStatus = this.normalizeStatus(status);
    switch (normalizedStatus) {
      case 'Active': return '#10B981';
      case 'On Leave': return '#F59E0B';
      case 'Inactive': return '#EF4444';
      default: return '#10B981';
    }
  }

  // Calculate doctor statistics
  static calculateStats(doctors: any[]) {
    const totalDoctors = doctors.length;
    
    // Helper function to check if a doctor is currently available
    const isDoctorCurrentlyAvailable = (doc: any): boolean => {
      if (doc.status !== 'Active') return false;
      
      const now = new Date();
      const currentDay = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][now.getDay()];
      
      // Check if today is in doctor's schedule
      if (!doc.schedule || !doc.schedule.includes(currentDay)) {
        return false; // Not available if not scheduled today
      }

      // Parse time slots (e.g., "9:00 AM - 6:00 PM")
      if (!doc.timeSlots) return false;
      
      const timeMatch = doc.timeSlots.match(/(\d{1,2}:\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}:\d{2})\s*(AM|PM)/i);
      if (!timeMatch) return false;

      const [_, startTime, startPeriod, endTime, endPeriod] = timeMatch;
      
      // Convert to 24-hour format
      const parseTime = (time: string, period: string): number => {
        const [hours, minutes] = time.split(':').map(Number);
        let hour24 = hours;
        if (period.toUpperCase() === 'PM' && hours !== 12) hour24 += 12;
        if (period.toUpperCase() === 'AM' && hours === 12) hour24 = 0;
        return hour24 * 60 + minutes;
      };

      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const startMinutes = parseTime(startTime, startPeriod);
      const endMinutes = parseTime(endTime, endPeriod);

      // Available if within working hours
      return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
    };
    
    // Count currently available doctors (Active status AND within schedule)
    const activeDoctors = doctors.filter(isDoctorCurrentlyAvailable).length;
    
    // Calculate off duty doctors - those who are Active but not currently available
    const offDuty = doctors.filter(doc => {
      return doc.status === 'Active' && !isDoctorCurrentlyAvailable(doc);
    }).length;
    
    const onLeave = doctors.filter(doc => doc.status === 'On Leave').length;
    const consultations = doctors.reduce((sum, doc) => sum + (doc.todayAppointments || 0), 0);
    const avgRating = doctors.length > 0 ? doctors.reduce((sum, doc) => sum + (doc.rating || 0), 0) / totalDoctors : 0;
    
    return {
      totalDoctors,
      activeDoctors,
      offDuty,
      onLeave,
      consultations,
      avgRating: avgRating.toFixed(1)
    };
  }
}

// Payment Management Service
class PaymentManagementService {
  static collectionName = 'payments';
  static invoicesCollectionName = 'invoices';

  // Get all payments with real-time data
  static async getPayments() {
    try {
      console.log('💰 Fetching payments from Firebase...');
      
      const paymentsRef = collection(db, this.collectionName);
      const q = query(paymentsRef, orderBy('date', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const payments: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        payments.push({
          id: doc.id,
          originalId: doc.id,
          ...data,
          // Normalize payment data
          patientName: data.patientName || 'Unknown Patient',
          patientId: data.patientId || doc.id,
          patientType: data.patientType || 'OP',
          amount: parseFloat(String(data.amount || data.totalAmount || 0)),
          totalAmount: parseFloat(String(data.totalAmount || data.amount || 0)),
          paidAmount: parseFloat(String(data.paidAmount || 0)),
          dueAmount: parseFloat(String(data.dueAmount || 0)),
          status: this.normalizePaymentStatus(data.status || 'pending'),
          paymentStatus: this.normalizePaymentStatus(data.paymentStatus || data.status || 'pending'),
          statusColor: this.getPaymentStatusColor(data.status || 'pending'),
          method: data.method || data.paymentMethod || 'Cash',
          description: data.description || data.purpose || 'Payment',
          lastPaymentDate: convertFirebaseTimestamp(data.date || data.createdAt),
          registrationDate: convertFirebaseTimestamp(data.registrationDate || data.createdAt),
          serviceType: data.serviceType || 'Consultation',
          type: data.type || 'payment',
          transactionId: data.transactionId || data.razorpayPaymentId,
          billBreakdown: data.items || data.billBreakdown || [
            { item: data.description || 'Consultation', amount: parseFloat(String(data.amount || 0)) }
          ]
        });
      });
      
      console.log(`💰 Fetched ${payments.length} payments from Firebase`);
      return payments;
    } catch (error) {
      console.error('❌ Error fetching payments:', error);
      return [];
    }
  }

  // Get invoices
  static async getInvoices() {
    try {
      console.log('🧾 Fetching invoices from Firebase...');
      
      const invoicesRef = collection(db, this.invoicesCollectionName);
      const q = query(invoicesRef, orderBy('createdAt', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const invoices: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        invoices.push({
          id: doc.id,
          invoiceNumber: data.invoiceNumber || `INV-${doc.id.slice(0, 8)}`,
          ...data,
          patientName: data.patientName || 'Unknown Patient',
          totalAmount: parseFloat(String(data.totalAmount || data.amount || 0)),
          paidAmount: parseFloat(String(data.paidAmount || 0)),
          dueAmount: parseFloat(String(data.dueAmount || data.totalAmount || 0)),
          status: this.normalizePaymentStatus(data.status || 'pending'),
          date: convertFirebaseTimestamp(data.createdAt || data.date),
          items: data.items || data.services || []
        });
      });
      
      console.log(`🧾 Fetched ${invoices.length} invoices from Firebase`);
      return invoices;
    } catch (error) {
      console.error('❌ Error fetching invoices:', error);
      return [];
    }
  }

  // Create new payment
  static async createPayment(paymentData: any) {
    try {
      console.log('💰 Creating payment in Firebase...', paymentData);
      
      const payment = {
        ...paymentData,
        status: paymentData.status || 'completed',
        createdAt: new Date(),
        date: paymentData.date || new Date().toISOString(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, this.collectionName), payment);
      console.log(`💰 Payment created with ID: ${docRef.id}`);
      
      return {
        success: true,
        data: {
          id: docRef.id,
          ...payment
        }
      };
    } catch (error: any) {
      console.error('❌ Error creating payment:', error);
      throw new Error(`Failed to create payment: ${error?.message || error}`);
    }
  }

  // Create new invoice
  static async createInvoice(invoiceData: any) {
    try {
      console.log('🧾 Creating invoice in Firebase...', invoiceData);
      
      const invoice = {
        ...invoiceData,
        invoiceNumber: invoiceData.invoiceNumber || `INV-${Date.now()}`,
        status: invoiceData.status || 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, this.invoicesCollectionName), invoice);
      console.log(`🧾 Invoice created with ID: ${docRef.id}`);
      
      return {
        success: true,
        data: {
          id: docRef.id,
          ...invoice
        }
      };
    } catch (error: any) {
      console.error('❌ Error creating invoice:', error);
      throw new Error(`Failed to create invoice: ${error?.message || error}`);
    }
  }

  // Update payment status
  static async updatePaymentStatus(paymentId: string, status: string) {
    try {
      console.log(`💰 Updating payment ${paymentId} status to ${status}`);
      
      const paymentRef = doc(db, this.collectionName, paymentId);
      await updateDoc(paymentRef, {
        status: status,
        paymentStatus: status,
        updatedAt: new Date()
      });
      
      console.log(`✅ Payment ${paymentId} status updated to ${status}`);
      return {
        success: true,
        message: `Payment ${status} successfully`
      };
    } catch (error: any) {
      console.error('❌ Error updating payment status:', error);
      throw new Error(`Failed to update payment status: ${error?.message || error}`);
    }
  }

  // Real-time payments listener
  static subscribeToPayments(callback: (payments: any[]) => void) {
    try {
      console.log('🔄 Setting up real-time payments listener...');
      
      const paymentsRef = collection(db, this.collectionName);
      const q = query(paymentsRef, orderBy('date', 'desc'));
      
      return onSnapshot(q, (querySnapshot) => {
        const payments: any[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          payments.push({
            id: doc.id,
            originalId: doc.id,
            ...data,
            // Normalize payment data
            patientName: data.patientName || 'Unknown Patient',
            patientId: data.patientId || doc.id,
            amount: parseFloat(String(data.amount || data.totalAmount || 0)),
            status: this.normalizePaymentStatus(data.status || 'pending'),
            statusColor: this.getPaymentStatusColor(data.status || 'pending'),
            method: data.method || data.paymentMethod || 'Cash',
            lastPaymentDate: convertFirebaseTimestamp(data.date || data.createdAt)
          });
        });
        
        console.log(`🔄 Real-time payments update: ${payments.length} payments`);
        callback(payments);
      });
    } catch (error) {
      console.error('❌ Error setting up payments listener:', error);
      throw error;
    }
  }

  // Helper methods
  private static normalizePaymentStatus(status: any): string {
    if (!status) return 'pending';
    
    const statusStr = String(status).toLowerCase();
    if (statusStr.includes('paid') || statusStr.includes('completed') || statusStr.includes('success')) {
      return 'completed';
    }
    if (statusStr.includes('pending') || statusStr.includes('processing')) {
      return 'pending';
    }
    if (statusStr.includes('failed') || statusStr.includes('cancelled') || statusStr.includes('refund')) {
      return 'failed';
    }
    return 'pending';
  }

  private static getPaymentStatusColor(status: any): string {
    const normalizedStatus = this.normalizePaymentStatus(status);
    switch (normalizedStatus) {
      case 'completed': return '#10B981';
      case 'pending': return '#F59E0B';
      case 'failed': return '#EF4444';
      default: return '#6B7280';
    }
  }

  // Calculate payment statistics
  static calculateStats(payments: any[]) {
    const totalPayments = payments.length;
    const completedPayments = payments.filter(p => p.status === 'completed').length;
    const pendingPayments = payments.filter(p => p.status === 'pending').length;
    const totalRevenue = payments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    const avgPayment = completedPayments > 0 ? totalRevenue / completedPayments : 0;
    
    return {
      totalPayments,
      completedPayments,
      pendingPayments,
      totalRevenue,
      avgPayment: Math.round(avgPayment)
    };
  }
}

// Reports and Analytics Service
class ReportsService {
  static async getHospitalReports() {
    try {
      console.log('📊 Generating hospital reports...');
      
      // Get all data collections with proper typing
      const [appointments, payments, patients, doctors] = await Promise.all([
        AppointmentManagementService.getAppointments(),
        PaymentManagementService.getPayments(),
        DashboardService.getPatients(),
        DashboardService.getDoctors()
      ]) as [any[], any[], any[], any[]];
      
      // Calculate comprehensive statistics
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      
      // Monthly revenue
      const monthlyRevenue = payments
        .filter(payment => {
          const paymentDate = new Date(payment.lastPaymentDate);
          return paymentDate.getMonth() === currentMonth && 
                 paymentDate.getFullYear() === currentYear &&
                 payment.status === 'completed';
        })
        .reduce((sum, payment) => sum + (payment.amount || 0), 0);
      
      // Patient statistics
      const totalPatients = patients.length;
      const newPatientsThisMonth = patients.filter((patient: any) => {
        const regDate = new Date(patient.registrationDate || patient.createdAt || '');
        return regDate.getMonth() === currentMonth && regDate.getFullYear() === currentYear;
      }).length;
      
      // Appointment statistics
      const totalAppointments = appointments.length;
      const todayAppointments = appointments.filter(apt => {
        const aptDate = new Date(apt.appointmentDate || apt.date || '');
        const today = new Date();
        return aptDate.toDateString() === today.toDateString();
      }).length;
      
      // Doctor utilization
      const activeDoctors = doctors.filter((doc: any) => doc.isActive !== false).length;
      const doctorAppointments = doctors.map((doctor: any) => ({
        name: doctor.name,
        appointments: appointments.filter((apt: any) => apt.doctorName === doctor.name).length,
        revenue: payments
          .filter((p: any) => p.description?.includes(doctor.name) && p.status === 'completed')
          .reduce((sum: number, p: any) => sum + (p.amount || 0), 0)
      }));
      
      // Department wise statistics
      const departmentStats = appointments.reduce((acc, apt) => {
        const dept = apt.department || 'General';
        if (!acc[dept]) {
          acc[dept] = { appointments: 0, revenue: 0 };
        }
        acc[dept].appointments++;
        const payment = payments.find(p => p.patientName === apt.patientName && p.status === 'completed');
        if (payment) {
          acc[dept].revenue += payment.amount || 0;
        }
        return acc;
      }, {} as any);
      
      // Revenue trend (last 6 months)
      const revenueTrend = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthName = date.toLocaleDateString('en-US', { month: 'short' });
        const monthRevenue = payments
          .filter(payment => {
            const paymentDate = new Date(payment.lastPaymentDate);
            return paymentDate.getMonth() === date.getMonth() && 
                   paymentDate.getFullYear() === date.getFullYear() &&
                   payment.status === 'completed';
          })
          .reduce((sum, payment) => sum + (payment.amount || 0), 0);
        
        revenueTrend.push({
          month: monthName,
          revenue: monthRevenue
        });
      }
      
      const report = {
        summary: {
          totalRevenue: payments
            .filter(p => p.status === 'completed')
            .reduce((sum, p) => sum + (p.amount || 0), 0),
          monthlyRevenue,
          totalPatients,
          newPatientsThisMonth,
          totalAppointments,
          todayAppointments,
          activeDoctors
        },
        doctorPerformance: doctorAppointments,
        departmentStatistics: Object.entries(departmentStats).map(([dept, stats]) => ({
          department: dept,
          ...(stats as any)
        })),
        revenueTrend,
        generatedAt: new Date().toISOString()
      };
      
      console.log('📊 Hospital reports generated successfully');
      return report;
    } catch (error) {
      console.error('❌ Error generating reports:', error);
      throw error;
    }
  }
  
  // Get patient medical reports
  static async getMedicalReports() {
    try {
      const reportsRef = collection(db, 'medicalReports');
      const q = query(reportsRef, orderBy('date', 'desc'));
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: convertFirebaseTimestamp(doc.data().date || doc.data().createdAt)
      }));
    } catch (error) {
      console.error('Error fetching medical reports:', error);
      return [];
    }
  }

  /**
   * Upload report file to Firebase Storage
   * @param file - The file to upload
   * @param patientId - The patient ID for organizing files
   * @param reportId - The report ID for unique identification
   * @returns Download URL of the uploaded file
   */
  static async uploadReportFile(file: File, patientId: string, reportId: string): Promise<string> {
    try {
      console.log(`📤 Uploading report file for patient ${patientId}, report ${reportId}...`);
      console.log(`📄 File details:`, { name: file.name, size: file.size, type: file.type });
      
      // Create a unique filename with timestamp
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `${reportId}_${timestamp}.${fileExtension}`;
      
      console.log(`📁 Storage path: medicalReports/${patientId}/${fileName}`);
      
      // Create storage reference: medicalReports/patientId/fileName
      const storageRef = ref(storage, `medicalReports/${patientId}/${fileName}`);
      
      console.log(`📤 Starting upload...`);
      // Upload file
      const snapshot = await uploadBytes(storageRef, file);
      console.log(`✅ File uploaded successfully: ${fileName}`);
      
      // Get download URL
      console.log(`🔗 Getting download URL...`);
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log(`✅ Download URL obtained: ${downloadURL}`);
      
      return downloadURL;
    } catch (error: any) {
      console.error('❌ Error uploading report file:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      throw new Error(`Failed to upload file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Upload multiple report files to Firebase Storage
   * @param files - Array of files to upload
   * @param patientId - The patient ID
   * @param reportId - The report ID
   * @returns Array of download URLs
   */
  static async uploadMultipleReportFiles(files: File[], patientId: string, reportId: string): Promise<string[]> {
    try {
      console.log(`📤 Uploading ${files.length} files for report ${reportId}...`);
      
      const uploadPromises = files.map(file => this.uploadReportFile(file, patientId, reportId));
      const downloadURLs = await Promise.all(uploadPromises);
      
      console.log(`✅ Successfully uploaded ${downloadURLs.length} files`);
      return downloadURLs;
    } catch (error) {
      console.error('❌ Error uploading multiple files:', error);
      throw error;
    }
  }

  /**
   * Create a new medical report with file uploads
   * @param reportData - Report information
   * @param files - Optional files to upload
   * @returns Created report with file URLs
   */
  static async createMedicalReport(reportData: any, files?: File[]): Promise<any> {
    try {
      console.log('📝 Creating new medical report...');
      console.log('👤 Current user:', auth.currentUser?.email || 'Not authenticated');
      console.log('📋 Report data:', reportData);
      
      // Check if user is authenticated
      if (!auth.currentUser) {
        throw new Error('User not authenticated. Please login to create reports.');
      }
      
      // If no files, create report immediately and return
      if (!files || files.length === 0) {
        const reportsRef = collection(db, 'medicalReports');
        console.log('💾 Creating report without files...');
        const newReportDoc = await addDoc(reportsRef, {
          ...reportData,
          status: 'pending',
          sentToPatient: false,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          fileUrls: [],
          fileUrl: ''
        });
        
        console.log(`✅ Report created without files with ID: ${newReportDoc.id}`);
        return {
          id: newReportDoc.id,
          ...reportData,
          fileUrls: [],
          fileUrl: ''
        };
      }
      
      // Create the report document to get the ID
      const reportsRef = collection(db, 'medicalReports');
      console.log('💾 Creating report document...');
      const newReportDoc = await addDoc(reportsRef, {
        ...reportData,
        status: 'pending',
        sentToPatient: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        fileUrls: [] // Will be updated after file upload
      });
      
      const reportId = newReportDoc.id;
      console.log(`✅ Report created with ID: ${reportId}`);
      
      // Upload files
      console.log(`📤 Uploading ${files.length} files for report...`);
      const fileUrls = await this.uploadMultipleReportFiles(files, reportData.patientId, reportId);
        
      // Update report with file URLs
      const reportRef = doc(db, 'medicalReports', reportId);
      const updateData = {
        fileUrls: fileUrls,
        fileUrl: fileUrls[0] || '', // Keep backward compatibility
        updatedAt: Timestamp.now()
      };
      
      console.log('💾 Updating Firestore with file URLs:', updateData);
      await updateDoc(reportRef, updateData);
      
      console.log(`✅ Report updated with ${fileUrls.length} file URLs`);
      console.log('📋 FileUrls array:', fileUrls);
      
      return {
        id: reportId,
        ...reportData,
        fileUrls,
        fileUrl: fileUrls[0] || ''
      };
    } catch (error: any) {
      console.error('❌ Error creating medical report:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // Provide specific error messages
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied: You must be logged in as an admin to create reports. Please check your authentication.');
      } else if (error.code === 'unauthenticated') {
        throw new Error('Not authenticated: Please login to create reports.');
      } else if (error.message?.includes('not authenticated')) {
        throw new Error('Authentication required: Please login to continue.');
      }
      
      throw error;
    }
  }

  /**
   * Send report to patient - marks as sent and stores in database
   * @param reportId - The report ID to send
   * @param patientId - The patient ID
   * @returns Success status
   */
  static async sendReportToPatient(reportId: string, patientId: string): Promise<boolean> {
    try {
      console.log(`📤 Sending report ${reportId} to patient ${patientId}...`);
      
      // Get the report
      const reportRef = doc(db, 'medicalReports', reportId);
      const reportSnap = await getDoc(reportRef);
      
      if (!reportSnap.exists()) {
        throw new Error('Report not found');
      }
      
      const reportData = reportSnap.data();
      
      // Verify the report belongs to the patient
      if (reportData.patientId !== patientId) {
        console.warn(`⚠️ Report patientId (${reportData.patientId}) doesn't match provided patientId (${patientId})`);
      }
      
      // Update report status to sent
      await updateDoc(reportRef, {
        sentToPatient: true,
        sentAt: new Date().toISOString(),
        status: 'sent',
        updatedAt: Timestamp.now()
      });
      
      console.log(`✅ Report ${reportId} marked as sent to patient ${patientId}`);
      console.log(`📱 Patient can now access this report in their dashboard`);
      
      // The report is already in medicalReports collection with sentToPatient=true
      // The patient's app will fetch reports where:
      // - patientId matches their ID
      // - sentToPatient === true
      
      return true;
    } catch (error) {
      console.error('❌ Error sending report to patient:', error);
      throw error;
    }
  }

  /**
   * Delete a report file from storage
   * @param fileUrl - The file URL to delete
   */
  static async deleteReportFile(fileUrl: string): Promise<void> {
    try {
      console.log(`🗑️ Deleting report file: ${fileUrl}`);
      const fileRef = ref(storage, fileUrl);
      await deleteObject(fileRef);
      console.log('✅ File deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting file:', error);
      throw error;
    }
  }

  /**
   * Create an admin report - simplified version for admin uploads to users
   * @param reportData - Report information
   * @param files - Files to upload
   * @returns Created report with file URLs
   */
  static async createAdminReport(reportData: any, files: File[]): Promise<any> {
    try {
      console.log('📝 Creating admin report...');
      console.log('👤 Current user:', auth.currentUser?.email || 'Not authenticated');
      
      if (!auth.currentUser) {
        throw new Error('User not authenticated. Please login to upload reports.');
      }

      if (!files || files.length === 0) {
        throw new Error('Please select at least one file to upload');
      }

      // Create the report document first to get the ID
      const reportsRef = collection(db, 'adminReports');
      const fileNames = files.map(f => f.name);
      
      const newReportDoc = await addDoc(reportsRef, {
        ...reportData,
        sentToPatient: true, // Auto-send to user
        sentAt: new Date().toISOString(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        fileUrls: [], // Will be updated after upload
        fileNames: fileNames // Store file names immediately
      });
      
      const reportId = newReportDoc.id;
      console.log(`✅ Report created with ID: ${reportId}`);
      
      // Upload files
      console.log(`📤 Uploading ${files.length} files...`);
      const fileUrls = await this.uploadMultipleReportFiles(files, reportData.patientId, reportId);
      
      console.log(`✅ Files uploaded, URLs:`, fileUrls);
      
      // Update report with file URLs
      const reportRef = doc(db, 'adminReports', reportId);
      await updateDoc(reportRef, {
        fileUrls: fileUrls,
        updatedAt: Timestamp.now()
      });
      
      console.log(`✅ Report updated with ${fileUrls.length} file URLs`);
      
      // Also create in medicalReports collection for mobile app compatibility
      console.log('📱 Creating report copy in medicalReports for mobile app...');
      const medicalReportsRef = collection(db, 'medicalReports');
      await addDoc(medicalReportsRef, {
        ...reportData,
        name: reportData.reportName, // Mobile app might use 'name' field
        title: reportData.reportName, // Alternative field name
        reportName: reportData.reportName, // Keep original field
        type: reportData.reportName, // CRITICAL: Mobile app checks 'type' first for display name
        reportId: reportId, // Reference to admin report
        sentToPatient: true,
        sentAt: new Date().toISOString(),
        date: new Date().toISOString(), // Add date field for mobile app
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        fileUrls: fileUrls,
        fileUrl: fileUrls[0] || '', // Backward compatibility - single file URL
        fileNames: fileNames,
        isRead: false, // For tracking read status in mobile app
        source: 'admin', // Indicate this came from admin
        status: 'sent' // Status field
      });
      
      console.log('✅ Report copied to medicalReports collection');
      
      // Create notification for patient
      console.log('🔔 Creating notification for patient...');
      const notificationsRef = collection(db, 'notifications');
      await addDoc(notificationsRef, {
        userId: reportData.patientId,
        type: 'report',
        title: 'New Medical Report Available',
        message: `Your ${reportData.category} report "${reportData.reportName}" is now available`,
        data: {
          reportId: reportId,
          reportName: reportData.reportName,
          category: reportData.category,
          fileUrls: fileUrls
        },
        isRead: false,
        createdAt: Timestamp.now(),
        timestamp: new Date().toISOString()
      });
      
      console.log('✅ Notification created for patient');
      
      return {
        id: reportId,
        ...reportData,
        fileUrls,
        fileNames,
        sentToPatient: true
      };
    } catch (error: any) {
      console.error('❌ Error creating admin report:', error);
      
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied: Please check your authentication.');
      } else if (error.code === 'unauthenticated') {
        throw new Error('Not authenticated: Please login to upload reports.');
      }
      
      throw error;
    }
  }

  /**
   * Resend an existing admin report to the user
   * @param reportId - The report ID
   * @param patientId - The patient ID
   * @returns Success status
   */
  static async resendAdminReport(reportId: string, patientId: string): Promise<boolean> {
    try {
      console.log(`📤 Resending admin report ${reportId} to patient ${patientId}...`);
      
      const reportRef = doc(db, 'adminReports', reportId);
      const reportSnap = await getDoc(reportRef);
      
      if (!reportSnap.exists()) {
        throw new Error('Report not found');
      }
      
      const reportData = reportSnap.data();
      
      // Update sent timestamp
      await updateDoc(reportRef, {
        sentToPatient: true,
        sentAt: new Date().toISOString(),
        updatedAt: Timestamp.now()
      });
      
      console.log(`✅ Report ${reportId} resent successfully`);
      
      // Update or create in medicalReports collection
      console.log('📱 Updating report in medicalReports for mobile app...');
      const medicalReportsRef = collection(db, 'medicalReports');
      const medicalReportsQuery = query(medicalReportsRef, where('reportId', '==', reportId));
      const existingReports = await getDocs(medicalReportsQuery);
      
      if (existingReports.empty) {
        // Create new entry if not exists
        await addDoc(medicalReportsRef, {
          ...reportData,
          name: reportData.reportName, // Mobile app might use 'name' field
          title: reportData.reportName, // Alternative field name
          reportName: reportData.reportName, // Keep original field
          type: reportData.reportName, // CRITICAL: Mobile app checks 'type' first for display name
          reportId: reportId,
          sentToPatient: true,
          sentAt: new Date().toISOString(),
          date: new Date().toISOString(), // Add date field for mobile app
          updatedAt: Timestamp.now(),
          fileUrl: reportData.fileUrls?.[0] || '', // Backward compatibility
          isRead: false,
          source: 'admin',
          status: 'sent'
        });
        console.log('✅ Report created in medicalReports collection');
      } else {
        // Update existing entry and reset read status
        const existingDoc = existingReports.docs[0];
        await updateDoc(doc(db, 'medicalReports', existingDoc.id), {
          name: reportData.reportName,
          title: reportData.reportName,
          reportName: reportData.reportName,
          type: reportData.reportName, // CRITICAL: Mobile app checks 'type' first for display name
          sentAt: new Date().toISOString(),
          date: new Date().toISOString(),
          updatedAt: Timestamp.now(),
          isRead: false // Reset read status on resend
        });
        console.log('✅ Report updated in medicalReports collection');
      }
      
      // Create new notification for resend
      console.log('🔔 Creating notification for patient...');
      const notificationsRef = collection(db, 'notifications');
      await addDoc(notificationsRef, {
        userId: patientId,
        type: 'report',
        title: 'Medical Report Resent',
        message: `Your ${reportData.category || 'medical'} report "${reportData.reportName}" has been resent`,
        data: {
          reportId: reportId,
          reportName: reportData.reportName,
          category: reportData.category,
          fileUrls: reportData.fileUrls || []
        },
        isRead: false,
        createdAt: Timestamp.now(),
        timestamp: new Date().toISOString()
      });
      
      console.log('✅ Notification created for patient');
      
      return true;
    } catch (error) {
      console.error('❌ Error resending report:', error);
      throw error;
    }
  }
}

// Services Management Service
class ServicesManagementService {
  static collectionName = 'hospitalServices';

  // Get all hospital services
  static async getServices() {
    try {
      console.log('🏥 Fetching hospital services from Firebase...');
      
      const servicesRef = collection(db, this.collectionName);
      const q = query(servicesRef, orderBy('name'));
      
      const querySnapshot = await getDocs(q);
      const services: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        services.push({
          id: doc.id,
          ...data,
          name: data.name || 'Unknown Service',
          description: data.description || '',
          price: parseFloat(String(data.price || data.fee || 0)),
          category: data.category || 'general',
          department: data.department || 'General',
          isActive: data.isActive !== false,
          duration: data.duration || '30 mins',
          assignedDoctors: data.assignedDoctors || [],
          createdAt: convertFirebaseTimestamp(data.createdAt)
        });
      });
      
      console.log(`🏥 Fetched ${services.length} services from Firebase`);
      return services;
    } catch (error) {
      console.error('❌ Error fetching services:', error);
      return [];
    }
  }

  // Create new service
  static async createService(serviceData: any) {
    try {
      console.log('🏥 Creating service in Firebase...', serviceData);
      
      const service = {
        ...serviceData,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, this.collectionName), service);
      console.log(`🏥 Service created with ID: ${docRef.id}`);
      
      return {
        success: true,
        data: {
          id: docRef.id,
          ...service
        }
      };
    } catch (error: any) {
      console.error('❌ Error creating service:', error);
      throw new Error(`Failed to create service: ${error?.message || error}`);
    }
  }

  // Update service
  static async updateService(serviceId: string, updateData: any) {
    try {
      console.log('🏥 Updating service in Firebase...', serviceId);
      
      const serviceRef = doc(db, this.collectionName, serviceId);
      await updateDoc(serviceRef, {
        ...updateData,
        updatedAt: new Date()
      });
      
      console.log('✅ Service updated successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating service:', error);
      throw error;
    }
  }

  // Delete service
  static async deleteService(serviceId: string) {
    try {
      console.log('🏥 Deleting service from Firebase...', serviceId);
      
      const serviceRef = doc(db, this.collectionName, serviceId);
      await updateDoc(serviceRef, {
        isActive: false,
        deletedAt: new Date(),
        updatedAt: new Date()
      });
      
      console.log('✅ Service marked as inactive');
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting service:', error);
      throw error;
    }
  }

  // Real-time services listener
  static subscribeToServices(callback: (services: any[]) => void) {
    try {
      console.log('🔄 Setting up real-time services listener...');
      
      const servicesRef = collection(db, this.collectionName);
      const q = query(servicesRef, orderBy('name'));
      
      return onSnapshot(q, (querySnapshot) => {
        const services: any[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          services.push({
            id: doc.id,
            ...data,
            name: data.name || 'Unknown Service',
            price: parseFloat(String(data.price || data.fee || 0)),
            category: data.category || 'general',
            isActive: data.isActive !== false
          });
        });
        
        console.log(`🔄 Real-time services update: ${services.length} services`);
        callback(services);
      });
    } catch (error) {
      console.error('❌ Error setting up services listener:', error);
      throw error;
    }
  }
}

// Tests Management Service
export class TestsManagementService {
  static collectionName = 'tests';

  // Get all tests
  static async getTests() {
    try {
      console.log('🧪 Fetching tests from Firebase...');
      
      const testsRef = collection(db, this.collectionName);
      const q = query(testsRef, orderBy('name'));
      
      const querySnapshot = await getDocs(q);
      const tests: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        tests.push({
          id: doc.id,
          ...data,
          name: data.name || 'Unknown Test',
          description: data.description || '',
          price: parseFloat(String(data.price || data.cost || 0)),
          category: data.category || 'diagnostic',
          department: data.department || 'Lab',
          isActive: data.isActive !== false,
          duration: data.duration || 'Same day',
          normalRange: data.normalRange || '',
          unit: data.unit || '',
          createdAt: convertFirebaseTimestamp(data.createdAt)
        });
      });
      
      console.log(`🧪 Fetched ${tests.length} tests from Firebase`);
      return tests;
    } catch (error) {
      console.error('❌ Error fetching tests:', error);
      return [];
    }
  }

  // Create new test
  static async createTest(testData: any) {
    try {
      console.log('🧪 Creating test in Firebase...', testData);
      
      const test = {
        ...testData,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, this.collectionName), test);
      console.log(`🧪 Test created with ID: ${docRef.id}`);
      
      return {
        success: true,
        data: {
          id: docRef.id,
          ...test
        }
      };
    } catch (error: any) {
      console.error('❌ Error creating test:', error);
      throw new Error(`Failed to create test: ${error?.message || error}`);
    }
  }
}

// Discharge Management Service
class DischargeManagementService {
  static collectionName = 'discharges';
  
  // Get all discharge records
  static async getDischarges() {
    try {
      console.log('🏥 Fetching discharge records from Firebase...');
      
      const dischargesRef = collection(db, this.collectionName);
      const q = query(dischargesRef, orderBy('dischargeDate', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const discharges: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        discharges.push({
          id: doc.id,
          ...data,
          patientName: data.patientName || 'Unknown Patient',
          patientId: data.patientId || doc.id,
          doctorName: data.doctorName || 'Unknown Doctor',
          department: data.department || 'General',
          admissionDate: convertFirebaseTimestamp(data.admissionDate),
          dischargeDate: convertFirebaseTimestamp(data.dischargeDate || data.createdAt),
          totalBill: parseFloat(String(data.totalBill || data.amount || 0)),
          status: data.status || 'completed',
          room: data.room || 'N/A',
          diagnosis: data.diagnosis || '',
          treatment: data.treatment || '',
          medications: data.medications || [],
          followUpDate: convertFirebaseTimestamp(data.followUpDate)
        });
      });
      
      console.log(`🏥 Fetched ${discharges.length} discharge records from Firebase`);
      return discharges;
    } catch (error) {
      console.error('❌ Error fetching discharge records:', error);
      return [];
    }
  }
  
  // Create new discharge record
  static async createDischarge(dischargeData: any) {
    try {
      console.log('🏥 Creating discharge record in Firebase...', dischargeData);
      
      const discharge = {
        ...dischargeData,
        status: 'completed',
        dischargeDate: dischargeData.dischargeDate || new Date(),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, this.collectionName), discharge);
      console.log(`🏥 Discharge record created with ID: ${docRef.id}`);
      
      return {
        success: true,
        data: {
          id: docRef.id,
          ...discharge
        }
      };
    } catch (error: any) {
      console.error('❌ Error creating discharge record:', error);
      throw new Error(`Failed to create discharge record: ${error?.message || error}`);
    }
  }
}

// Export all services
export {
  AdminAuthService,
  DashboardService,
  PaymentManagementService,
  DoctorManagementService,
  ServicesManagementService,
  AppointmentManagementService,
  ReportsService,
  PatientService,
  DischargeManagementService
};

// Default export for backward compatibility
export default {
  AdminAuthService,
  DashboardService,
  PaymentManagementService,
  DoctorManagementService,
  ServicesManagementService,
  AppointmentManagementService,
  ReportsService,
  PatientService,
  DischargeManagementService
};